import { IChecklistItem } from "@mctypes/checklist.types";
import { CommentActions, IComment, IEditorMetadata } from "@mctypes/global.types";
import { Card, Title } from "@mantine/core";
import React from "react";
import FoldableSection from "../../../../../components/FoldableSection/FoldableSection";
import { setShowSuccessMessageThunk } from "../../../../../global/globalSlice";
import { useAppDispatch, useAppSelector } from "../../../../../hooks";
import { ToastMessagesEnums } from "@mcapps/shared/sharedConstants";
import LocalErrorBoundary from "../../../../../util/LocalErrorBoundary";
import { usePutProjectByProjectIdChecklistItemAndChecklistItemIdCommentCommentIdReactionMutation } from "../../../../../../openapi";
import MultiCommentMantine from "../../../issuesApp/IssuesTab/IssueItemModal/new/MultiCommentMantine";
import { doCreateComment, doDeleteComment, doUpdatecomment } from "../../../projects/restQueries";
import { fetchSingleChecklistItemAndReplaceInReduxThunk, updateSingleChecklistItemInRedux } from "../../checklistSlice";

const ChecklistComments: React.FC<{ comments: IComment[]; checklistItemId: string }> = ({
  comments,
  checklistItemId,
}) => {
  const checklist = useAppSelector((state) => state.checklistReducer.checklist);

  const user = useAppSelector((state) => state.globalReducer.user);

  const dispatch = useAppDispatch();
  const [commentLoading, setCommentLoading] = React.useState(false);
  const [reactToComment] =
    usePutProjectByProjectIdChecklistItemAndChecklistItemIdCommentCommentIdReactionMutation();

  const project = useAppSelector((state) => state.adminReducer.project);

  const commentAction = async (
    commentId: string,
    boardId: string,
    action: CommentActions,
    value?: string,
    metadata?: IEditorMetadata,
  ) => {
    setCommentLoading(true);

    try {
      switch (action) {
        case "ADD":
          await doCreateComment(project._id, checklistItemId, "checklistItem", value, boardId, metadata);
          await dispatch(fetchSingleChecklistItemAndReplaceInReduxThunk(checklistItemId));
          break;
        case "DELETE":
          await doDeleteComment(project._id, commentId);
          await dispatch(fetchSingleChecklistItemAndReplaceInReduxThunk(checklistItemId));
          break;
        case "UPDATE":
          await doUpdatecomment(project._id, commentId, value);
          await dispatch(fetchSingleChecklistItemAndReplaceInReduxThunk(checklistItemId));
          break;
        default:
          return;
      }

      dispatch(setShowSuccessMessageThunk(ToastMessagesEnums.UPDATED));
    } finally {
      setCommentLoading(false);
    }
  };

  return (
    <FoldableSection storageKey="checklist-modal-comments">
      <FoldableSection.Header>
        <Title order={6}>Diskusjon</Title>
      </FoldableSection.Header>
      <FoldableSection.Body>
        <LocalErrorBoundary id="ChecklistDiscussionSection">
          <Card w="100%" bg="#f8f9fa" bd="1px solid var(--mantine-color-warmgray-2)">
            <MultiCommentMantine
              isLoading={commentLoading}
              showCancelButton={false}
              members={project?.members}
              comments={comments}
              onAdd={async (text, metadata) => {
                await commentAction(null, checklist._id, "ADD", text, metadata);
              }}
              onDelete={async (_id) => {
                await commentAction(_id, checklist._id, "DELETE");
              }}
              onUpdate={async (_id, text, metadata) => {
                await commentAction(_id, checklist._id, "UPDATE", text, metadata);
              }}
              onReact={async (commentId, emoji) => {
                const updatedItem = await reactToComment({
                  projectId: project._id,
                  checklistItemId,
                  commentId,
                  body: { emoji },
                }).unwrap();
                dispatch(updateSingleChecklistItemInRedux(updatedItem as unknown as IChecklistItem));
              }}
              currentUserId={user?._id}
              loggedInUser={user}
            />
          </Card>
        </LocalErrorBoundary>
      </FoldableSection.Body>
    </FoldableSection>
  );
};

export default ChecklistComments;
